import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tallyService } from '../services';
import { useAuthStore } from '../store';
import type {
  OutstandingResponse,
  PaymentInitiateRequest,
  PaymentSuccessRequest,
  PaymentFailureRequest,
  PaymentHistoryResponse,
  BalanceResponse,
  EffectiveBalanceResponse
} from '../types';

// Query Keys
export const QUERY_KEYS = {
  outstanding: (flatId: string) => ['outstanding', flatId] as const,
  paymentHistory: (flatId: string, limit: number) => ['paymentHistory', flatId, limit] as const,
  balance: (flatId: string) => ['balance', flatId] as const,
  effectiveBalance: (flatId: string) => ['effectiveBalance', flatId] as const,
  paymentStatus: (requestId: string) => ['paymentStatus', requestId] as const,
} as const;

// Outstanding Charges Query
export const useOutstandingCharges = () => {
  const { user, isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: QUERY_KEYS.outstanding(user?.activeFlatId || ''),
    queryFn: () => tallyService.getOutstandingCharges(user!.activeFlatId),
    enabled: isAuthenticated && !!user?.activeFlatId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      // Don't retry on authentication errors
      if (error.message.includes('session has expired') || error.message.includes('permission')) {
        return false;
      }
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Payment History Query
export const usePaymentHistory = (limit: number = 10) => {
  const { user, isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: QUERY_KEYS.paymentHistory(user?.activeFlatId || '', limit),
    queryFn: () => tallyService.getPaymentHistory(user!.activeFlatId, limit),
    enabled: isAuthenticated && !!user?.activeFlatId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Balance Query (legacy)
export const useBalance = () => {
  const { user, isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: QUERY_KEYS.balance(user?.activeFlatId || ''),
    queryFn: () => tallyService.getBalance(user!.activeFlatId),
    enabled: isAuthenticated && !!user?.activeFlatId,
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 3 * 60 * 1000, // 3 minutes
  });
};

// Effective Balance Query (recommended)
export const useEffectiveBalance = () => {
  const { user, isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: QUERY_KEYS.effectiveBalance(user?.activeFlatId || ''),
    queryFn: () => tallyService.getEffectiveBalance(user!.activeFlatId),
    enabled: isAuthenticated && !!user?.activeFlatId,
    staleTime: 30 * 1000, // 30 seconds (more frequent for real-time updates)
    gcTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 60 * 1000, // Auto-refresh every 60 seconds
  });
};

// Payment Status Query
export const usePaymentStatus = (paymentRequestId: string | null) => {
  return useQuery({
    queryKey: QUERY_KEYS.paymentStatus(paymentRequestId || ''),
    queryFn: () => tallyService.getPaymentStatus(paymentRequestId!),
    enabled: !!paymentRequestId,
    refetchInterval: (query) => {
      // Stop polling if payment is complete
      if (query.state.data?.status === 'SUCCESS' || query.state.data?.status === 'FAILED') {
        return false;
      }
      return 3000; // Poll every 3 seconds for pending payments
    },
    staleTime: 0, // Always fresh for payment status
  });
};

// Payment Initiate Mutation
export const useInitiatePayment = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: (request: PaymentInitiateRequest) => tallyService.initiatePayment(request),
    onSuccess: () => {
      // Invalidate outstanding charges as they might change
      if (user?.activeFlatId) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.outstanding(user.activeFlatId)
        });
      }
    },
  });
};

// Payment Success Mutation
export const usePaymentSuccess = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: (request: PaymentSuccessRequest) => tallyService.handlePaymentSuccess(request),
    onSuccess: () => {
      if (user?.activeFlatId) {
        // Invalidate and refetch all payment-related queries
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.outstanding(user.activeFlatId)
        });
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.balance(user.activeFlatId)
        });
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.effectiveBalance(user.activeFlatId)
        });
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.paymentHistory(user.activeFlatId, 10)
        });
      }
    },
  });
};

// Payment Failure Mutation
export const usePaymentFailure = () => {
  return useMutation({
    mutationFn: (request: PaymentFailureRequest) => tallyService.handlePaymentFailure(request),
  });
};

// Helper hook to refresh all payment data
export const useRefreshPaymentData = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return () => {
    if (user?.activeFlatId) {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.outstanding(user.activeFlatId)
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.balance(user.activeFlatId)
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.effectiveBalance(user.activeFlatId)
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.paymentHistory(user.activeFlatId, 10)
      });
    }
  };
};

// Optimistic updates helper
export const useOptimisticPaymentUpdate = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return {
    updateOutstandingCharges: (updater: (old: OutstandingResponse) => OutstandingResponse) => {
      if (user?.activeFlatId) {
        queryClient.setQueryData(
          QUERY_KEYS.outstanding(user.activeFlatId),
          updater
        );
      }
    },

    updateBalance: (updater: (old: BalanceResponse) => BalanceResponse) => {
      if (user?.activeFlatId) {
        queryClient.setQueryData(
          QUERY_KEYS.balance(user.activeFlatId),
          updater
        );
      }
    },

    updateEffectiveBalance: (updater: (old: EffectiveBalanceResponse) => EffectiveBalanceResponse) => {
      if (user?.activeFlatId) {
        queryClient.setQueryData(
          QUERY_KEYS.effectiveBalance(user.activeFlatId),
          updater
        );
      }
    },

    addPaymentToHistory: (payment: any) => {
      if (user?.activeFlatId) {
        queryClient.setQueryData(
          QUERY_KEYS.paymentHistory(user.activeFlatId, 10),
          (old: PaymentHistoryResponse | undefined) => {
            if (!old) return old;
            
            return {
              ...old,
              transactions: [payment, ...old.transactions.slice(0, 9)], // Keep only 10 items
              totalTransactions: old.totalTransactions + 1,
              totalAmountPaid: old.totalAmountPaid + payment.amount
            };
          }
        );
      }
    }
  };
};